import {OFFLINE_STORES} from './offlineStorageAdapter.js';
import {collectLegacyRecoverySources, legacyHash, legacyPositiveId, legacySourceFingerprint} from './legacyRecoverySources.js';
import {offlineFileBytes} from './offlineFileVault.js';

const STORE = OFFLINE_STORES.CONTEXTS;
const pathOf = raw => raw.filePath ?? raw.file_path;
const scope = user => `${legacyPositiveId(user?.id)}:${legacyPositiveId(user?.empresa_id)}`;
const keyOf = fingerprint => `legacy-source-copy:${fingerprint}`;
function eligible(item, user) {
    const r = item.raw;
    return item.type === 'MEDIA' && legacyPositiveId(user?.id) && legacyPositiveId(user?.empresa_id)
        && legacyPositiveId(r.userId ?? r.user_id) === Number(user.id)
        && legacyPositiveId(r.empresaId ?? r.empresa_id) === Number(user.empresa_id)
        && legacyPositiveId(r.visitaId ?? r.visita_id) && typeof pathOf(r) === 'string' && /^content:\/\//i.test(pathOf(r))
        && typeof r.sha256 === 'string' && /^[a-f0-9]{64}$/.test(r.sha256)
        && Number.isSafeInteger(r.size) && r.size > 0 && r.size <= 100 * 1024 * 1024
        && ![r.file, r.blob, r.data, r.base64, r.bytes].some(value => value != null);
}
export async function listLegacySourceCopyCandidates({user, collect = collectLegacyRecoverySources}) {
    const sources = await collect();
    if (sources.errors.length) throw Error('El inventario anterior no pudo comprobarse completo.');
    return sources.records.filter(item => eligible(item, user));
}

// Una selección nueva aporta bytes comprobados; jamás reemplaza la URI, fila,
// UUID ni intención de origen. Este journal no es un archivo activo ni un ACK.
export async function preserveLegacySourceCopy({user, fingerprint, file, driver, fs, sessionProvider,
    collect = collectLegacyRecoverySources}) {
    const authorized = async () => {
        const session = await sessionProvider();
        if (!legacyPositiveId(user?.id) || !legacyPositiveId(user?.empresa_id)
            || scope(session?.user) !== scope(user) || !(Number(session?.offline_expires_at) > Date.now()))
            throw Error('La cuenta o su autorización cambió. La recuperación sigue pendiente.');
    };
    await authorized(); await driver.init();
    const candidates = await listLegacySourceCopyCandidates({user, collect});
    const item = candidates.find(row => row.fingerprint === fingerprint);
    if (!item || await legacySourceFingerprint(item.source, item.raw) !== fingerprint) throw Error('El origen cambió o no tiene una huella comprobable.');
    const bytes = await offlineFileBytes(file);
    if (bytes.length !== item.raw.size || await legacyHash(bytes) !== item.raw.sha256)
        throw Error('El archivo elegido no coincide con el tamaño y la huella del original.');
    const id = keyOf(fingerprint), path = `legacy-source-copies/${user.id}/${fingerprint}.bin`;
    const row = {id, kind: 'legacy_source_copy', userId: Number(user.id), empresaId: Number(user.empresa_id),
        visitaId: Number(item.raw.visitaId ?? item.raw.visita_id), sourceFingerprint: fingerprint,
        sourcePath: pathOf(item.raw), path, size: bytes.length, sha256: item.raw.sha256, version: fingerprint,
        scope: 'legacy-source-copy', templateVersion: fingerprint};
    const previous = await driver.get(STORE, id);
    if (previous && JSON.stringify(previous) !== JSON.stringify(row)) throw Error('Ya existe otra evidencia para este origen.');
    let present = false;
    try {await fs.stat({path, directory: 'DATA'}); present = true;} catch (error) {
        if (!/not (?:exist|found)|does not exist|ENOENT|no existe|0008/i.test(String(error?.message))) throw error;
    }
    if (!present) {
        await fs.mkdir({path: `legacy-source-copies/${user.id}`, directory: 'DATA', recursive: true});
        let binary = '';
        for (let offset = 0; offset < bytes.length; offset += 32768) binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
        await fs.writeFile({path, directory: 'DATA', data: btoa(binary)});
    }
    const copied = await offlineFileBytes((await fs.readFile({path, directory: 'DATA'})).data);
    if (copied.length !== row.size || await legacyHash(copied) !== row.sha256) throw Error('La copia privada no coincide; se conserva pendiente sin sobrescribirla.');
    await authorized();
    const current = (await listLegacySourceCopyCandidates({user, collect})).find(r => r.fingerprint === fingerprint);
    if (!current || await legacySourceFingerprint(current.source, current.raw) !== fingerprint) throw Error('El origen cambió durante la copia.');
    await driver.mutate(STORE, id, value => {
        if (value && JSON.stringify(value) !== JSON.stringify(row)) throw Error('La evidencia cambió durante la copia.');
        return value ? null : row;
    });
    if (JSON.stringify(await driver.get(STORE, id)) !== JSON.stringify(row)) throw Error('No se confirmó la evidencia de lectura.');
    return {preserved: true, fingerprint, size: row.size, sha256: row.sha256};
}

export function withLegacySourceCopies({user, records, driver, fs}) {
    return {
        ...fs,
        readFile: async args => {
            try {return await fs.readFile(args);} catch (originalError) {
                const matches = records.filter(item => eligible(item, user) && pathOf(item.raw) === args.path);
                if (!matches.length) throw originalError;
                let result;
                for (const item of matches) {
                    if (await legacySourceFingerprint(item.source, item.raw) !== item.fingerprint) throw originalError;
                    const row = await driver.get(STORE, keyOf(item.fingerprint));
                    if (!row || row.kind !== 'legacy_source_copy' || row.userId !== Number(user.id)
                        || row.empresaId !== Number(user.empresa_id) || row.visitaId !== Number(item.raw.visitaId ?? item.raw.visita_id)
                        || row.sourceFingerprint !== item.fingerprint || row.sourcePath !== args.path
                        || row.path !== `legacy-source-copies/${user.id}/${item.fingerprint}.bin`
                        || row.size !== item.raw.size || row.sha256 !== item.raw.sha256) throw originalError;
                    const copy = await fs.readFile({path: row.path, directory: 'DATA'}), bytes = await offlineFileBytes(copy.data);
                    if (bytes.length !== row.size || await legacyHash(bytes) !== row.sha256) throw Error('La copia seleccionada ya no coincide con el original.');
                    if (result && await legacyHash(await offlineFileBytes(result.data)) !== row.sha256) throw Error('La misma URI identifica contenidos anteriores diferentes.');
                    result = copy;
                }
                return result;
            }
        },
        mkdir: args => fs.mkdir(args), stat: args => fs.stat(args), writeFile: args => fs.writeFile(args),
    };
}
